import { useState } from 'react';
import { PermissionEnum } from '@ui/src';
import { useAppSelector } from '@store';
import { RiskScoringTypes, CustomerType } from '../../../hoc/customers/types';
import ExpandableRowContent from '../../partial/aml/expandable-row-content';
import ExpandableRow from '../../partial/aml/expandable-row';
import TotalRiskScoring from '../../partial/aml/total-risk-scoring';
import DeliveryChannels from '../../partial/aml/delivery-channels';
import Products from '../../partial/aml/products';
import SicCodes from '../../partial/aml/sic-codes';
import DateOfIncorporation from '../../partial/aml/date-of-incorporation';
import { AMLRiskScoringTypes } from '../types';

import cn from './aml.module.css';

const RiskScoring = ({
  isEditable,
  aml,
  onUpdate,
  updateInProgress,
  amlUpdateStatus,
  updateIsSuccess,
  calculateAllowed,
  status,
  isLoading,
  users,
  setManualScoreSidepanelVisible,
  setRiskScoreChangeLogsSidepanelVisible,
  manualScoreAllowed,
}: AMLRiskScoringTypes): React.JSX.Element => {
  const [expandedRows, setExpandedRows] = useState<string[]>([
    RiskScoringTypes.DELIVERY_CHANNEL,
    RiskScoringTypes.GEOGRAPHICAL_RISK,
    RiskScoringTypes.PRODUCTS,
    RiskScoringTypes.SIC_CODES,
    RiskScoringTypes.DATE_ON_INCORPORATION,
    RiskScoringTypes.MONTHLY_VOLUMES,
  ]);

  const [blockCollapsed, setBlockCollapsed] = useState(false);

  const onExpandClick = () => {
    if (expandedRows.length) {
      setExpandedRows([]);
    } else {
      setExpandedRows([
        RiskScoringTypes.DELIVERY_CHANNEL,
        RiskScoringTypes.GEOGRAPHICAL_RISK,
        RiskScoringTypes.PRODUCTS,
        RiskScoringTypes.SIC_CODES,
        RiskScoringTypes.DATE_ON_INCORPORATION,
        RiskScoringTypes.MONTHLY_VOLUMES,
      ]);
    }
  };

  const { countries, monthlyTurnoverCorporate, activePermissions } =
    useAppSelector((state) => state.customersDictionaries);

  return (
    <div className={cn.riskScoringContent}>
      <TotalRiskScoring
        aml={aml}
        // logs={aml?.totalRisk.totalRiskScoreLogs}
        updateIsSuccess={updateIsSuccess}
        updateInProgress={updateInProgress}
        onUpdate={onUpdate}
        onCollapse={() => setBlockCollapsed(!blockCollapsed)}
        blockCollapsed={blockCollapsed}
        amlUpdateStatus={amlUpdateStatus}
        calculateAllowed={calculateAllowed}
        status={status}
        isLoading={isLoading}
        users={users}
        manualScoreAllowed={manualScoreAllowed}
        setManualScoreSidepanelVisible={setManualScoreSidepanelVisible}
        changeLogsAvailable={activePermissions?.includes(
          PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST,
        )}
        setRiskScoreChangeLogsSidepanelVisible={
          setRiskScoreChangeLogsSidepanelVisible
        }
      />
      {blockCollapsed && (
        <>
          <div className={cn.collapseButtonWrapper}>
            <button
              type="button"
              className={cn.collapseAllButton}
              onClick={onExpandClick}
            >
              {!expandedRows?.length ? 'Expand all' : 'Collapse all'}
            </button>
          </div>
          <ExpandableRow
            name={RiskScoringTypes.GEOGRAPHICAL_RISK}
            title="Geographical risk"
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            totalRisk={aml?.geographicalRiskTotal as number}
            // comments={[]}
            // onSend={() => {}}
            // onDelete={() => {}}
          >
            <ExpandableRowContent
              title="Country of incorporation:"
              value={aml?.countryOfIncorporationId as string}
              risk={
                aml?.countryOfIncorporationRisk?.toString() !== 'null' &&
                aml?.countryOfIncorporationRisk?.toString() !== 'undefined'
                  ? (aml?.countryOfIncorporationRisk?.toString() as string)
                  : '-'
              }
              list={countries}
              name="countryOfIncorporation"
              isEditable={isEditable}
            />
            <ExpandableRowContent
              title="Citizenship of company officials"
              value=""
              risk={
                aml?.officialRiskTotal?.toString() !== 'null' &&
                aml?.officialRiskTotal?.toString() !== 'undefined'
                  ? (aml?.officialRiskTotal?.toString() as string)
                  : '-'
              }
              list={countries}
              name="citizenshipOfCompanyOfficials"
              isEditable={isEditable}
              officialRiskItems={aml?.officialRiskItems}
              withoutOpacity
            />
            {/* <ExpandableRowContent
              title="Relevant countries"
              value={aml?.countryId as string}
              risk={aml?.countryRisk as number}
              name="relevantCountries"
              list={countries}
              isEditable={isEditable}
            /> */}
          </ExpandableRow>
          <DeliveryChannels
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
            type={CustomerType.CORPORATE}
          />
          <Products
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
            type={CustomerType.CORPORATE}
          />
          <DateOfIncorporation
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
          />
          <SicCodes
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            values={aml}
            // onSend={() => {}}
            // onDelete={() => {}}
            isEditable={isEditable}
          />
          <ExpandableRow
            name={RiskScoringTypes.MONTHLY_VOLUMES}
            title="Turnover"
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            totalRisk={aml?.monthlyTurnoverRangeRisk as number}

            // comments={[]}
            // onSend={() => {}}
            // onDelete={() => {}}
          >
            <ExpandableRowContent
              title="Turnover:"
              value={aml?.monthlyTurnoverRangeId as string}
              risk={
                aml?.monthlyTurnoverRangeRisk?.toString() !== 'null' &&
                aml?.monthlyTurnoverRangeRisk?.toString() !== 'undefined'
                  ? (aml?.monthlyTurnoverRangeRisk?.toString() as string)
                  : '-'
              }
              list={monthlyTurnoverCorporate}
              name="monthlyTurnoverRangeId"
              isEditable={isEditable}
            />
          </ExpandableRow>
        </>
      )}
    </div>
  );
};

export default RiskScoring;
